import IMAGES from '@/assets/images';
import CustomImage from '@/components/CustomImage';
import { XIcon } from '@/icons';
import { addZero, errorToast } from '@/utils';
import { Button, Empty, Flex, Skeleton } from 'antd';
import { useState } from 'react';
import {
  useAssignShiftSingleUserMutation,
  useGetShiftDetailsQuery,
  useUnAssignShiftSingleUserMutation,
} from '../../../../../../app/features/employee/emp-attendance/emp-attendance.api';
import { useGetAllEmployeesQuery } from '../../../../../../app/features/employee/emp-dashboard/emp-dashboard.api';
import ConfirmationModal from '../../../../../../components/ConfirmationModal';
import useAuth from '../../../../../../hooks/useAuth';
import useError from '../../../../../../hooks/useError';
import useModal from '../../../../../../hooks/useModal';
import { toast } from '../../../../../../lib/toast';
import SelectWithInfo from './SelectWithInfo';

type Props = {
  shiftId: string;
};

export default function AssignedList({ shiftId }: Props) {
  const { tenantId, organizationId } = useAuth();
  const { open, handleOpen, handleClose } = useModal<string, 'CONFIRM'>();
  const [removingUserId, setRemovingUserId] = useState<string | null>(null);

  const [assignShift, { isLoading: isAssignLoading }] =
    useAssignShiftSingleUserMutation();
  const [unassignShift] = useUnAssignShiftSingleUserMutation();

  const { data: employeesData } = useGetAllEmployeesQuery({
    params: {
      skip: 0,
      limit: 0,
      tenant_id: tenantId,
    },
  });
  const { isLoading, isFetching, error, data } = useGetShiftDetailsQuery(
    shiftId,
    {
      skip: !shiftId,
    },
  );
  useError(error, 'Failed to fetch shift details');

  const loading = isLoading || isFetching;
  const shiftData = data?.data;

  const employees =
    employeesData?.data?.data?.map((emp) => ({
      id: emp.user_id,
      name: emp.name,
      empId: emp.employee_id,
      role: emp.designation?.name,
      avatar: emp.photo || undefined,
    })) || [];

  // Get assigned users from shift data
  const assignedUsers = (shiftData as any)?.userList || [];

  // Map assigned users to include their full details
  const assignedEmployees = assignedUsers?.map((user: any) => {
    return {
      id: user.user_id,
      name: user.name,
      empId: user.employee_id,
      role: user?.department_name,
      avatar: user?.photo || undefined,
    };
  });

  const handleAssignShift = async (userId: string) => {
    try {
      await assignShift({
        tenantId,
        organizationId,
        shiftId,
        userId,
      }).unwrap();
      toast('success', 'Shift assigned successfully for the next 90 days');
    } catch (err) {
      errorToast(err, 'Failed to assign shift');
    }
  };

  const handleUnassignShift = async (userId: string) => {
    setRemovingUserId(userId);
    try {
      await unassignShift({
        organizationId,
        userId,
      }).unwrap();
      toast('success', 'User unassigned from shift successfully');
    } catch (err) {
      errorToast(err, 'Failed to unassign user from shift');
    } finally {
      setRemovingUserId(null);
      handleClose();
    }
  };

  return (
    <div>
      <SelectWithInfo
        data={employees}
        className="w-full"
        size="middle"
        popupMatchSelectWidth={false}
        onChange={(userId) => {
          if (userId) {
            handleAssignShift(userId as string);
          }
        }}
        loading={isAssignLoading}
        disabled={isAssignLoading}
      />

      <div className="mt-6">
        <h6 className="dark:text-white text-lg font-bold mb-6">
          Assigned ({loading ? '...' : addZero(assignedUsers.length)})
        </h6>
        <div className="space-y-5 max-h-[300px] overflow-y-auto pr-2 custom-scroll">
          {loading ? (
            // Skeleton loading
            <>
              {[...Array(3)].map((_, idx) => (
                <SkeletonItem key={idx} />
              ))}
            </>
          ) : assignedEmployees.length > 0 ? (
            assignedEmployees.map((employee: any) => (
              <Item
                key={employee.id}
                onRemove={() => {
                  handleOpen({
                    type: 'CONFIRM',
                    data: employee.id,
                  });
                }}
                isRemoving={removingUserId === employee.id}
                {...employee}
              />
            ))
          ) : (
            <Empty />
          )}
        </div>
      </div>

      {/* Confirmation Modal */}
      <ConfirmationModal
        data={open.data}
        type={open.type}
        handleClose={handleClose}
        handleSubmit={() => {
          if (open?.data) {
            handleUnassignShift(open.data);
          }
        }}
        open={open.open && open.type === 'CONFIRM'}
        loading={removingUserId === open.data}
        title="Are you sure?"
        subTitle="You want to unassign this user?"
        buttonName="Delete"
      />
    </div>
  );
}

type TData = {
  id: string;
  avatar?: string;
  name: string;
  empId?: number | string;
  role?: string;
  onRemove: (id: string) => void;
  isRemoving?: boolean;
};

const Item = ({
  id,
  avatar,
  name,
  empId,
  role,
  onRemove,
  isRemoving,
}: TData) => {
  return (
    <Flex className="border-b pb-3 border-theme-gray-700" gap={24}>
      {/* {avatar ? (
        <Avatar
          src={avatar || PLACEHOLDER_IMG}
          alt={name}
          className="w-14 h-14 rounded-full object-cover"
        />
      ) : (
        <span className="size-14 inline-flex justify-center items-center rounded-full border-[0.86px] dark:bg-gray-300 dark:border-gray-200 dark:text-theme-gray-800 font-semibold text-lg border-teal-500">
          {firstChar(name)}
        </span>
      )} */}
      <CustomImage
        size={56}
        className="rounded-full object-cover"
        src={avatar || IMAGES.avatar}
      />

      <Flex justify="space-between" align="center" className="w-full flex-1">
        <div className="text-left dark:text-white">
          {empId && <p className="text-xs font-light">ID {empId}</p>}
          <p className="font-medium text-lg">{name}</p>
          {role && <p className="text-sm">{role}</p>}
        </div>

        <Button
          size="small"
          type="text"
          variant="outlined"
          className="!size-7 border border-theme-slate-800"
          classNames={{ icon: 'flex items-center justify-center' }}
          onClick={() => onRemove(id)}
          loading={isRemoving}
          disabled={isRemoving}
          icon={!isRemoving && <XIcon />}
        />
      </Flex>
    </Flex>
  );
};

// Skeleton loader component
const SkeletonItem = () => (
  <Flex className="border-b pb-3 border-theme-gray-700" gap={24}>
    <Skeleton.Avatar active size={56} shape="circle" />

    <Flex justify="space-between" align="center" className="w-full flex-1">
      <div className="space-y-2 flex-1 flex flex-col">
        <Skeleton.Input active size="small" style={{ width: 60 }} />
        <Skeleton.Input active size="default" style={{ width: 150 }} />
        <Skeleton.Input active size="small" style={{ width: 120 }} />
      </div>

      <Skeleton.Button
        active
        size="small"
        shape="default"
        style={{ width: 32, height: 32 }}
      />
    </Flex>
  </Flex>
);
